import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {groupApi, rolesApi, userApi} from 'api';
import {AssignmentSelectors, Button, Card, DataWrapper, MetricsPieChart} from 'components';
import {UserContext} from 'context';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {UserRole} from 'types';
import {dateUtils} from 'utils';
import {formatDate} from 'utils/transform';

import {MetricUsers} from './MetricUsers';
import {UsersAvailabilityMetric, UsersAvailabilityMetricConfig} from './types';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.USERS.USERS_AVAILABILITY;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

export const UsersAvailability: React.FC = () => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat, timeFormat}}} = useContext(UserContext);
    const [selectedGroupId, setSelectedGroupId] = useState<string>(null);

    const {
        send: getUsersAvailability,
        loading: isAvailabilityLoading,
        data: usersAvailability,
        loading,
        initialLoading,
        loadedAt,
        error
    } = useApiData(userApi.getUsersAvailability, {loadingDefault: true});

    const {send: getGroups, data: groups} = useApiData(groupApi.getGroups);
    const {send: getRoles, data: roles} = useApiData<UserRole[]>(rolesApi.getRoles);

    useEffect(() => {
        getUsersAvailability();
        getGroups();
        getRoles();
    }, [getUsersAvailability, getGroups, getRoles]);

    const totalConfigs = useMemo(() => [
        UsersAvailabilityMetricConfig[UsersAvailabilityMetric.Online],
        UsersAvailabilityMetricConfig[UsersAvailabilityMetric.Offline],
        UsersAvailabilityMetricConfig[UsersAvailabilityMetric.Away],
    ], []);
    const onlineConfigs = useMemo(() => [
        UsersAvailabilityMetricConfig[UsersAvailabilityMetric.InSession],
        UsersAvailabilityMetricConfig[UsersAvailabilityMetric.NotInSession],
    ], []);

    const formattedDate = useMemo(() =>
        formatDate(loadedAt, timeZone, dateUtils.getDateTimeFormat(dateFormat, timeFormat)), [loadedAt, timeZone, dateFormat, timeFormat]);

    const [activeMetric, setActiveMetric] = useState(totalConfigs[0].metric);

    const onMetricClick = useCallback((metric: UsersAvailabilityMetric) => {
        setActiveMetric(metric);
    }, []);

    const handleGroupChange = useCallback((groupId: string) => {
        setSelectedGroupId(groupId);
        getUsersAvailability(groupId);
    }, [getUsersAvailability]);

    const handleRefresh = useCallback(() => {
        getUsersAvailability(selectedGroupId);
    }, [selectedGroupId, getUsersAvailability]);

    return (
        <div>
            {loadedAt &&
                <div className='md:tw-hidden tw-mb-4'>{`${t(COMMON_T_KEYS.ALL.LOADED_AT)} ${formattedDate}`}</div>}
            <div className='tw-mb-8 tw-flex tw-items-end'>
                <div className='tw-inline-block tw-w-full tw-max-w-72'>
                    <AssignmentSelectors
                        disabled={loading}
                        selectedGroupId={selectedGroupId}
                        groups={groups}
                        collapseEmptyOption={false}
                        emptyGroupOption='all'
                        onChange={handleGroupChange}
                    >
                        {({renderGroup}) => <div>{renderGroup()}</div>}
                    </AssignmentSelectors>
                </div>
                <div className='tw-inline-flex tw-items-center tw-ml-auto'>
                    {loadedAt &&
                        <div className='-md:tw-hidden'>{`${t(COMMON_T_KEYS.ALL.LOADED_AT)} ${formattedDate}`}</div>}
                    <Button
                        className='tw-ml-2'
                        disabled={loading}
                        icon='refresh'
                        width='small'
                        onClick={handleRefresh}
                    >
                        {t(COMMON_T_KEYS.BUTTONS.REFRESH)}
                    </Button>
                </div>
            </div>
            <div className='tw-relative'>
                <DataWrapper
                    isError={!!error}
                    isLoading={isAvailabilityLoading}
                    hideContentOnLoading={initialLoading || !usersAvailability}
                >
                    <div className='tw-text-lg md:tw-text-3xl md:tw-text-bold tw-mb-4'>{t(COMMON_T_KEYS.ALL.PIE_CHARTS)}</div>
                    <Card>
                        <div className='tw-grid md:tw-grid-cols-2 tw-grid-cols-1'>
                            <MetricsPieChart
                                title={t(T_KEYS.PIE_CHARTS.TOTAL)}
                                data={usersAvailability}
                                activeMetric={activeMetric}
                                configs={totalConfigs}
                                onMetricClick={onMetricClick}
                            />
                            <MetricsPieChart
                                title={t(T_KEYS.PIE_CHARTS.ONLINE)}
                                data={usersAvailability}
                                activeMetric={activeMetric}
                                configs={onlineConfigs}
                                onMetricClick={onMetricClick}
                            />
                        </div>
                    </Card>
                    <MetricUsers
                        usersAvailability={usersAvailability}
                        groups={groups}
                        roles={roles}
                        metric={activeMetric}
                    />
                </DataWrapper>
            </div>
        </div>
    );
};